import { Fragment, type ReactNode, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { useDraftMode } from "../../../contexts/DraftModeContext";
import { useRequestsContext } from "../../../contexts/RequestsContext";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useErrorSnackbar } from "../../../hooks/useErrorSnackbar";
import { useHandleNurseChange } from "../../../hooks/useHandleNurseChange";
import { useHandleRequest } from "../../../hooks/useHandleRequest";
import { usePrepareNurseShift } from "../../../hooks/usePrepareNurseShift";
import { useRosterLookup } from "../../../hooks/useRosterLookup";
import { useScheduleDayOf } from "../../../hooks/useScheduleDayOf";
import { useScheduledShiftLookup } from "../../../hooks/useScheduledShiftLookup";
import { useScheduleSaves } from "../../../hooks/useScheduleSaves";
import { useFetchScheduleWeeks } from "../../../hooks/useScheduleWeeks";
import { backendService } from "../../../services/backend.service";
import { trackErrorEvent, trackEvent } from "../../../utils/analytics";
import type { StaffingIssue, Suggestion } from "../../../utils/autoStaffing";
import { findScheduleSlot, type ScheduleDayShift, type ScheduleSlot } from "../../../utils/departmentWeeks";
import type { BaseRequest, Nurse, NurseFullRole, NurseInShift, OpenShiftRequest, ReplaceShiftRequest, ShiftTimes } from "../../../utils/models";
import { IssuePriority, IssueType, NotificationType, NurseInShiftCreationSource, RequestResponsesStatusOptions, SuggestionAction } from "../../../utils/models";
import { acceptedHoursOf, getShortRole } from "../../../utils/utilFunctions";
import SystemDialog from "../../UIToolkit/Popups/Dialog";
import { RequestUtilsContext } from "./useRequestUtils";

export enum AgencyReplacementChoice {
	CancelAgency = "cancel_agency",
	KeepAgency = "keep_agency",
}

/**
 * What an approval did, resolved by `approveRequest` once its writes AND its requests refresh are done. Existing callers
 * ignore it; a caller that must act on the result (the gaps approve sheet hands on to the assign-unit sheet) reads it
 * here rather than off provider state, which the refresh's last `setState` has not committed by the time the awaited
 * call returns.
 */
export enum ApproveRequestOutcome {
	/** The approval was written (or, in draft mode, applied locally). */
	Applied = "applied",
	/** Nothing was written: a failed write, missing data, or a request type this provider does not approve. The
	 * provider has already said why where there was something to say. */
	NotApplied = "not_applied",
	/** The keep-or-cancel-agency choice was dismissed, so the user chose to approve nothing. */
	Dismissed = "dismissed",
}

export const RequestUtilsProvider = ({ children }: { children: ReactNode }) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { addNurse, isLoading } = useHandleNurseChange();
	const { replaceRequest } = useHandleRequest();
	const { refreshRequests } = useRequestsContext();
	const { chosenDepartment } = useChosenDepartment();
	const showErrorSnackbar = useErrorSnackbar();
	const { roster, getNurseById, getNurseFormattedName } = useRosterLookup(chosenDepartment.department_id);
	const { addNurseToShift, removeNurseFromSpecificDate, getShiftScheduleDates, afterScheduleWrite, oldListSlotOf } = useScheduleSaves();
	const prepareNurseShift = usePrepareNurseShift();
	const scheduleDayOf = useScheduleDayOf();
	const findScheduledShift = useScheduledShiftLookup();
	const fetchScheduleWeeks = useFetchScheduleWeeks();
	const { addNurse: isAddNurseLoading } = isLoading;
	const { isDraftMode } = useDraftMode();
	// The cancel-vs-keep choice lives here rather than in a dialog component because every approval
	// entry point (maximized + minimized approval dialog, Requests tab, Recommendations, manual add)
	// funnels through approveRequest — gating it anywhere else leaves a path that silently cancels
	// the agency.
	const [pendingAgencyChoice, setPendingAgencyChoice] = useState<{ request: OpenShiftRequest | ReplaceShiftRequest; nurseId: number } | null>(null);
	const agencyChoiceResolver = useRef<((choice: AgencyReplacementChoice | null) => void) | null>(null);

	const askAgencyReplacementChoice = (request: OpenShiftRequest | ReplaceShiftRequest, nurseId: number): Promise<AgencyReplacementChoice | null> =>
		new Promise((resolve) => {
			agencyChoiceResolver.current = resolve;
			setPendingAgencyChoice({ request, nurseId });
		});

	const resolveAgencyReplacementChoice = (choice: AgencyReplacementChoice | null) => {
		if (pendingAgencyChoice) {
			trackEvent("replace_agency_choice", {
				request_id: pendingAgencyChoice.request.id,
				nurse_id: pendingAgencyChoice.nurseId,
				choice: choice ?? "dismissed",
			});
		}
		setPendingAgencyChoice(null);
		agencyChoiceResolver.current?.(choice);
		agencyChoiceResolver.current = null;
	};

	const approveReplaceAgencyRequest = async (
		request: OpenShiftRequest | ReplaceShiftRequest,
		nurseId: number,
		location?: string,
	): Promise<ApproveRequestOutcome> => {
		const choice = await askAgencyReplacementChoice(request, nurseId);
		// Dismissed — approve nothing.
		if (!choice) return ApproveRequestOutcome.Dismissed;

		if (choice === AgencyReplacementChoice.KeepAgency) {
			// A plain add leaves the agency shift live, which is how the backend recognizes the keep
			// and opens the next replacement request for it.
			await approveAddRequest(nurseId, request, location);
			return ApproveRequestOutcome.Applied;
		}
		return approveReplaceRequest(request as ReplaceShiftRequest, nurseId);
	};

	/** The write for one request type; each resolves what it did, or throws once it has shown why it failed. */
	const writeApproval = async (request: OpenShiftRequest | ReplaceShiftRequest, nurseId: number, location?: string): Promise<ApproveRequestOutcome | null> => {
		switch (request.request_type) {
			case NotificationType.OpenShift:
				await approveAddRequest(nurseId, request, location);
				return ApproveRequestOutcome.Applied;
			case NotificationType.ReplaceShift:
				return approveReplaceRequest(request as ReplaceShiftRequest, nurseId);
			case NotificationType.ReplaceAgency:
				return approveReplaceAgencyRequest(request, nurseId, location);
			case NotificationType.Swap:
				return approveSwapRequest(request as ReplaceShiftRequest);
			default:
				// Not a type this provider approves: nothing is written and, as before, nothing is refreshed.
				return null;
		}
	};

	const approveRequest = async (request: OpenShiftRequest | ReplaceShiftRequest, nurseId: number, location?: string): Promise<ApproveRequestOutcome> => {
		let outcome: ApproveRequestOutcome | null;
		try {
			outcome = await writeApproval(request, nurseId, location);
		} catch {
			return ApproveRequestOutcome.NotApplied;
		}
		if (outcome === null) return ApproveRequestOutcome.NotApplied;

		// Refreshed whatever the write resolved (a refused swap and a dismissed agency choice included), exactly as before.
		if (!isDraftMode) {
			await refreshRequests();
		}
		return outcome;
	};

	/** The shift approving `nurseId` puts them on; undefined when they aren't on the department roster. */
	const prepareRequestShift = (
		nurseId: number,
		{ date, shiftType }: ScheduleDayShift,
		location: string | undefined,
		requestId: string,
		shiftTimes: ShiftTimes,
		shiftBonus: number | undefined,
	): NurseInShift | undefined => {
		const nurse = getNurseById(nurseId);
		return (
			nurse &&
			prepareNurseShift({
				nurse,
				date,
				shiftType,
				location,
				addedFromActual: false,
				requestId,
				shiftTimes,
				nurseInShiftId: undefined,
				shiftBonus,
				isDraftMode: false,
			})
		);
	};

	const approveAddRequest = async (nurseId: number, request: OpenShiftRequest | ReplaceShiftRequest, location?: string): Promise<void> => {
		// A request's date is its shift's start; the slot is the schedule day that start belongs to.
		const scheduleDay = scheduleDayOf({ startTime: request.message.shift_start, shiftType: request.message.shift_type });

		const bonus = isPartialShift(request, nurseId) ? 0 : (request.current_bonus ?? request.bonus);

		const nurseApproveItem = request.message.request_data.find((d) => d.nurse_id === nurseId);
		const shiftTimes = {
			...acceptedHoursOf(request, nurseApproveItem),
			date: nurseApproveItem?.accepted_start_time ?? request.message.date,
		};

		const nurseToAdd = prepareRequestShift(nurseId, scheduleDay, location ?? request.location, request.id, shiftTimes, bonus);
		if (!nurseToAdd) {
			showErrorSnackbar(t("error.approve.request"), { nurse_id: nurseId, request_id: request.id, reason: "nurse not on the department roster" });
			throw new Error(`Nurse ${nurseId} is not on the department roster`);
		}

		if (isDraftMode) {
			// Set requestId on nurse so auto-tracking can detect this is from a request approval
			nurseToAdd.requestId = request.id;

			addNurseToShift(nurseToAdd, scheduleDay.date, scheduleDay.shiftType, false, isDraftMode);

			showSnackbar(
				`${t("nurse.added.success", {
					nurseName: nurseToAdd?.name,
					day: nurseToAdd?.startTime.toFormat("ccc"),
					shiftType: t(nurseToAdd?.shiftType),
				})} (Draft Mode)`,
				"info",
			);

			return;
		}

		try {
			await addNurse({
				nurse: nurseToAdd,
				location: nurseToAdd.location,
				...scheduleDay,
				requestId: request.id,
				shiftTimes: {
					startTime: shiftTimes.start,
					endTime: shiftTimes.end,
				},
				source: NurseInShiftCreationSource.Requests,
			});

			addNurseToShift(nurseToAdd, scheduleDay.date, scheduleDay.shiftType);
			showSnackbar(
				t("nurse.added.success", {
					nurseName: nurseToAdd?.name,
					day: nurseToAdd?.startTime.toFormat("ccc"),
					shiftType: t(nurseToAdd?.shiftType),
				}),
				"success",
			);
		} catch (e) {
			showErrorSnackbar("Failed to approve request", { nurseId, request }, e);
			throw e;
		}
	};

	const isPartialShift = (request: BaseRequest, nurseId: number): boolean => {
		const reqData = request.message.request_data.find((d) => d.nurse_id === nurseId && d.status === RequestResponsesStatusOptions.PENDING);
		const shiftStart = request.message.shift_start;
		const shiftEnd = request.message.shift_end;
		return (
			!!reqData?.accepted_start_time &&
			!!reqData?.accepted_end_time &&
			!(reqData?.accepted_start_time.hasSame(shiftStart, "minute") && reqData?.accepted_end_time.hasSame(shiftEnd, "minute"))
		);
	};

	const recomendationFromRequests = (request: OpenShiftRequest | ReplaceShiftRequest): StaffingIssue => {
		const date = request.message.date;
		const dayString = date.toFormat("cccc");
		const monthDayString = date.toFormat("MMMM Do");

		const shiftType = request.message.shift_type;
		const shiftStart = request.message.shift_start;
		const shiftEnd = request.message.shift_end;

		const fullText = `${dayString} ${shiftType} ${monthDayString} sss`;
		// const rolesText = 'Roles: ' + (notification.message.roles.length > 0 ? ` (${notification.message.roles.join(', ')})` : '');
		const rolesText =
			request.message.roles.length > 0 ? ` (${request.message.roles.map((role) => getShortRole(role as NurseFullRole) || role).join(", ")})` : "";
		let neededText = "";
		if (request.request_type === NotificationType.ReplaceShift.valueOf()) {
			if (request.message.request_data.some((s) => s.status === RequestResponsesStatusOptions.PENDING)) {
				neededText = "1 nurse needed";
			} else {
				neededText = "No nurses accepted yet";
			}
		} else if (request.request_type === NotificationType.OpenShift.valueOf()) {
			const OpenShiftRequest = request as OpenShiftRequest;
			const acceptedNursesCount = request.message.request_data.filter((r) => r.status === RequestResponsesStatusOptions.APPROVED)?.length || 0;
			const remainingNeeded = OpenShiftRequest.needed_nurses - acceptedNursesCount;
			neededText = remainingNeeded > 1 ? `${remainingNeeded} nurses needed` : "1 nurse needed";
		}

		const nurse = roster?.find((rosterNurse: Nurse) => rosterNurse.userId === request.created_by);

		return {
			issueType: IssueType.Shift,
			title: [
				{
					text: request.request_type === NotificationType.ReplaceShift && nurse ? getNurseFormattedName(nurse.id) : "",
				},
				{ text: "wants to replace their shift on ", type: "normal" },
				{ text: fullText, type: "bold" },
				{ text: rolesText, type: "normal" },
				{ text: neededText, type: "normal" },
			],
			priority: IssuePriority.TopPriority as IssuePriority,
			shiftTimes: {
				start: shiftStart,
				end: shiftEnd,
				date: date,
			},
			notificationType: request.request_type,
			suggestions: suggestionFromRequest(request, nurse),
			notificationId: request.id,
		};
	};

	const suggestionFromRequest = (request: BaseRequest, nurse: Nurse | undefined): Suggestion[] => {
		const suggeststionsArr: Suggestion[] = [];
		const requestDay = scheduleDayOf({ startTime: request.message.shift_start, shiftType: request.message.shift_type });
		// Positions in the old list, which only the Week view's highlights read; approving goes by the request's date.
		const requestSlot = oldListSlotOf(requestDay.date, requestDay.shiftType);
		// Only show suggestions if there are status
		for (const data of request.message.request_data.filter((r) => r.status === RequestResponsesStatusOptions.PENDING)) {
			const nurseId = request.request_type === NotificationType.ReplaceShift ? (nurse?.id ?? -1) : data.nurse_id;
			if (nurseId === -1) {
				// when we have a replace shift request we expect a nurse to be provided - if we dont have it we cant create a suggestion
				trackErrorEvent(
					"suggestionFromRequest_replaceShift_noNurse",
					{},
					{
						requestId: request.id,
						shiftId: (request as ReplaceShiftRequest).request_for_shift,
					},
				);
			} else {
				suggeststionsArr.push({
					action: request.request_type === NotificationType.OpenShift ? SuggestionAction.Add : SuggestionAction.Replace,
					// DEFAULT: -1 for a request outside the loaded weeks, as before; accepting it reports the day isn't loaded.
					shiftNo: requestSlot?.shiftIndex ?? -1,
					dayNo: requestSlot?.dayIndex ?? -1,
					otherNurseId: data.nurse_id,
					nurseId: nurseId,
					displayReject: true,
					weekIndex: requestSlot?.weekIndex ?? -1,
					shiftId: (request as ReplaceShiftRequest).request_for_shift,
					requestId: request.id,
				});
			}
		}

		return suggeststionsArr;
	};

	const approveSwapRequest = async (request: ReplaceShiftRequest): Promise<ApproveRequestOutcome> => {
		if (!request.request_for_shift || !request.request_target_shift) {
			showErrorSnackbar("Failed to approve swap request - missing shift information", {
				request_id: request.id,
			});
			return ApproveRequestOutcome.NotApplied;
		}

		try {
			await backendService.approveAndSwapRequest(request.id, chosenDepartment.department_id);
			// The swap moves two shifts server-side with nothing applied locally, so only a reload puts it on the
			// grid — the add/replace writes already reload after publishing, and the approval dialog relies on that.
			if (!isDraftMode) {
				const swappedDates = getShiftScheduleDates([request.request_for_shift, request.request_target_shift]);
				void afterScheduleWrite(swappedDates === null ? { allWeeks: true } : { dates: swappedDates });
			}

			showSnackbar("Swap request approved successfully", "success");
			return ApproveRequestOutcome.Applied;
		} catch (e) {
			showErrorSnackbar("Failed to approve swap request", { request_id: request.id }, e);
			throw e;
		}
	};

	const approveReplaceRequest = async (request: ReplaceShiftRequest, nurseId: number): Promise<ApproveRequestOutcome> => {
		// A request's date is its shift's start; the slot is the schedule day that start belongs to.
		const scheduleDay = scheduleDayOf({ startTime: request.message.shift_start, shiftType: request.message.shift_type });

		// Backward compatability support: older requests name the nurse, not the shift.
		let nurseToRemove: NurseInShift | undefined;
		let requestSlot: ScheduleSlot | undefined;
		try {
			nurseToRemove = await findScheduledShift(
				request.request_for_shift ? { ...scheduleDay, shiftId: request.request_for_shift } : { ...scheduleDay, nurseId: request.replacement_nurse_id },
			);
			requestSlot = findScheduleSlot(await fetchScheduleWeeks(scheduleDay.date), scheduleDay.date, scheduleDay.shiftType);
		} catch (e) {
			showErrorSnackbar(t("staffing.changes.schedule.load.failed"), { nurse_id: nurseId, request_id: request.id }, e);
			return ApproveRequestOutcome.NotApplied;
		}

		if (!nurseToRemove) {
			showErrorSnackbar("Failed to approve request - missing nurse", { nurse_id: nurseId, request_id: request.id });
			return ApproveRequestOutcome.NotApplied;
		}
		// The replacement is added to the request's own slot; without it the local schedule couldn't show the swap.
		if (!requestSlot) {
			showErrorSnackbar(t("error.approve.request"), { nurse_id: nurseId, request_id: request.id, reason: "request day not loaded" });
			return ApproveRequestOutcome.NotApplied;
		}

		const bonus = isPartialShift(request, nurseId) ? 0 : (request.current_bonus ?? request.bonus);

		const nurseApproveItem = request.message.request_data.find((d) => d.nurse_id === nurseId);
		const shiftTimes = {
			...acceptedHoursOf(request, nurseApproveItem),
			date: nurseApproveItem?.accepted_start_time ?? request.message.date,
		};

		const nurseToAdd = prepareRequestShift(nurseId, scheduleDay, undefined, request.id, shiftTimes, bonus);
		if (!nurseToAdd) {
			showErrorSnackbar(t("error.approve.request"), { nurse_id: nurseId, request_id: request.id, reason: "nurse not on the department roster" });
			return ApproveRequestOutcome.NotApplied;
		}

		nurseToAdd.location = nurseToRemove.location;
		nurseToRemove.replacedWith = nurseToAdd.nurseId;

		if (isDraftMode) {
			// Set requestId on replacement nurse so auto-tracking can detect this is from a request approval
			nurseToAdd.requestId = request.id;

			removeNurseFromSpecificDate(scheduleDay.date, scheduleDay.shiftType, nurseToRemove.nurseId, false, nurseToRemove.id, isDraftMode);
			addNurseToShift(nurseToAdd, scheduleDay.date, scheduleDay.shiftType, false, isDraftMode);

			showSnackbar(`${t("success.request.approved")} (Draft Mode)`, "info");
			return ApproveRequestOutcome.Applied;
		}

		try {
			await replaceRequest({
				nurseToAdd,
				nurseToRemove,
				requestId: request.id,
				date: request.message.date,
			});

			removeNurseFromSpecificDate(scheduleDay.date, scheduleDay.shiftType, nurseToRemove.nurseId, false, nurseToRemove.id);
			addNurseToShift(nurseToAdd, scheduleDay.date, scheduleDay.shiftType);
			showSnackbar(t("success.request.approved"), "success");
			return ApproveRequestOutcome.Applied;
		} catch (e) {
			showErrorSnackbar("Failed to approve request", { nurse_id: nurseToAdd.id, request_id: request?.id }, e);
			throw e;
		}
	};

	return (
		<Fragment>
			<RequestUtilsContext.Provider
				value={{
					approveRequest,
					approveAddRequest,
					recomendationFromRequests,
					suggestionFromRequest,
					isPartialShift,
					isApproveAddRequestLoading: isAddNurseLoading,
				}}
			>
				{children}
			</RequestUtilsContext.Provider>
			<SystemDialog
				open={!!pendingAgencyChoice}
				onClose={() => resolveAgencyReplacementChoice(null)}
				title={t("replace.agency")}
				body={t("replace.agency.choice.body", {
					// DEFAULT: an agency nurse assigned by hand (rather than through a Nursa/Clipboard posting)
					// carries no agency_source_name. Naming the action generically still describes the choice
					// correctly, and blocking the dialog over a missing label would strand the approval.
					agencyName: (pendingAgencyChoice?.request as OpenShiftRequest | undefined)?.agency_source_name ?? t("replace.agency"),
					internalName: pendingAgencyChoice ? getNurseFormattedName(pendingAgencyChoice.nurseId) : "",
				})}
				confirm={t("replace.agency.choice.cancel.agency")}
				onConfirm={() => resolveAgencyReplacementChoice(AgencyReplacementChoice.CancelAgency)}
				secondaryAction={{
					label: t("replace.agency.choice.keep.agency"),
					onClick: () => resolveAgencyReplacementChoice(AgencyReplacementChoice.KeepAgency),
				}}
				dataTestId="replace-agency-confirm-dialog"
			/>
		</Fragment>
	);
};
